// Today's nominations, with each one's vote count and where it stands — and, for a Storyteller,
// one-click Execute for the current leader and Exile for any Traveler whose exile passed.
//
// The rule this panel exists to make legible: only the single highest-vote nomination that clears
// the threshold can be executed, and if the top spot is tied nobody is executed that day. Exiles
// stand apart from that race.

import { leadingNomination } from "@cc/shared";
import type { GameView, Nomination } from "@cc/shared";
import { useGame } from "../../state/game.js";
import { cn } from "../../lib/ui.js";
import { Button } from "../ui/primitives.js";
import { Panel } from "./Panel.js";

function outcomeLabel(nomination: Nomination): string {
  switch (nomination.outcome) {
    case "executed":
      return "executed";
    case "exiled":
      return "exiled";
    case "on_the_block":
      return nomination.exile ? "exile passed" : "on the block";
    case "short":
      return "short of the threshold";
    case "cancelled":
      return "withdrawn";
    default:
      return nomination.status === "voting" ? "voting" : "on the floor";
  }
}

export function Nominations({
  view,
  open,
  onOpenChange,
}: {
  view: GameView;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const send = useGame((s) => s.send);
  const today = view.nominations.filter((n) => n.day === view.phase.number);
  const closed = today.filter((n) => n.status === "closed" && !n.exile);
  const { leader, tied } = leadingNomination(
    closed.map((n) => ({ id: n.id, nomineeSeatId: n.nomineeSeatId, tally: n.tally })),
    view.voteThreshold,
  );
  const name = (seatId: string): string =>
    view.seats.find((s) => s.id === seatId)?.displayName ?? "a former player";

  return (
    <Panel
      title="Today's Nominations"
      badge={<span className="text-sm text-parchment-dim">needs {view.voteThreshold}</span>}
      open={open}
      onOpenChange={onOpenChange}
    >
      {today.length === 0 ? (
        <p className="py-2 text-sm text-parchment-dim">Nothing nominated yet today.</p>
      ) : (
        <ul className="space-y-1.5">
          {today.map((nomination) => {
            const isLeader = leader?.id === nomination.id;
            return (
              <li
                key={nomination.id}
                className={cn(
                  "rounded border px-2 py-1.5",
                  isLeader ? "border-brass/70 bg-brass/10" : "border-ink-line",
                )}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="truncate text-sm text-parchment">
                    {name(nomination.nomineeSeatId)}
                    <span className="text-parchment-dim">
                      {nomination.exile ? " exile called by " : " by "}
                      {name(nomination.nominatorSeatId)}
                    </span>
                  </span>
                  <span className="shrink-0 text-sm tabular-nums text-brass">
                    {nomination.status === "closed" ? nomination.tally : "—"}
                  </span>
                </div>
                <div className="text-xs text-parchment-dim">
                  {outcomeLabel(nomination)}
                  {nomination.exile && ` · needs ${nomination.threshold}`}
                </div>

                {view.you.isStoryteller && nomination.exile && nomination.outcome === "on_the_block" && (
                  <Button
                    size="sm"
                    variant="danger"
                    className="mt-1"
                    onClick={() => void send("st:exile", { nominationId: nomination.id })}
                  >
                    Exile {name(nomination.nomineeSeatId)}
                  </Button>
                )}

                {view.you.isStoryteller && nomination.status !== "closed" && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="mt-1"
                    onClick={() => void send("st:closeNomination", { nominationId: nomination.id })}
                  >
                    {nomination.status === "voting" ? "Close vote now" : "Close without a vote"}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {view.you.isStoryteller && (
        <div className="mt-2 border-t border-ink-line pt-2">
          {view.executedSeatId ? (
            <p className="text-xs text-parchment-dim">
              {name(view.executedSeatId)} has already been executed today.
            </p>
          ) : tied ? (
            <p className="text-xs text-parchment-dim">The top two are tied — nobody is executed today.</p>
          ) : leader ? (
            <Button
              variant="danger"
              size="sm"
              className="w-full"
              onClick={() => void send("st:execute", { nominationId: leader.id })}
            >
              Execute {name(leader.nomineeSeatId)}
            </Button>
          ) : (
            <p className="text-xs text-parchment-dim">
              No nomination has reached {view.voteThreshold} vote(s) yet.
            </p>
          )}
        </div>
      )}
    </Panel>
  );
}
